import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import pt, { type Dictionary } from './locales/pt';
import * as fmt from './format';
import { setCurrentCopy } from './current';
import {
  DEFAULT_LOCALE,
  HTML_LANG,
  localizedPath,
  persistLocale,
  resolveLocale,
  type Locale,
} from './locale';

/**
 * Dicionário tipado em vez de i18next.
 *
 * A cópia é conteúdo ESTRUTURADO e o tipo `Dictionary` (derivado do português)
 * garante em tempo de compilação que nenhum idioma tem chave faltando. Com
 * i18next o acesso seria uma string que o TypeScript não confere.
 *
 * Os arquivos de locale são objetos simples, então viram `resources` do i18next
 * sem reescrita se um dia o projeto precisar de TMS externo, fallback parcial
 * por chave ou um quarto idioma. A porta continua aberta.
 */

/**
 * Só o português é estático: ele é a fonte do tipo e o idioma padrão, então
 * precisa estar disponível na primeira renderização. Inglês e espanhol vêm por
 * import dinâmico para não jogar dois dicionários inteiros no chunk crítico de
 * quem nunca vai lê-los.
 */
const LOADERS: Record<Exclude<Locale, 'pt-BR'>, () => Promise<{ default: Dictionary }>> = {
  en: () => import('./locales/en'),
  es: () => import('./locales/es'),
};

const cache = new Map<Locale, Dictionary>([[DEFAULT_LOCALE, pt]]);

async function loadDictionary(locale: Locale): Promise<Dictionary> {
  const cached = cache.get(locale);
  if (cached) return cached;
  const loader = LOADERS[locale as Exclude<Locale, 'pt-BR'>];
  if (!loader) return pt;
  const mod = await loader();
  cache.set(locale, mod.default);
  return mod.default;
}

interface LocaleContextValue {
  locale: Locale;
  copy: Dictionary;
  /** Prefixa uma rota interna com o idioma atual. */
  href: (path: string) => string;
  /** Troca o idioma e persiste a escolha. */
  setLocale: (next: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children, locale: forced }: { children: React.ReactNode; locale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => forced ?? resolveLocale());
  const [copy, setCopy] = useState<Dictionary>(() => cache.get(locale) ?? pt);
  const [ready, setReady] = useState(() => cache.has(locale));

  useEffect(() => {
    if (cache.has(locale)) {
      setCopy(cache.get(locale)!);
      setReady(true);
      return;
    }
    let alive = true;
    setReady(false);
    void loadDictionary(locale).then((dict) => {
      if (!alive) return;
      setCopy(dict);
      setReady(true);
    });
    return () => { alive = false; };
  }, [locale]);

  // Antes de qualquer filho renderizar: os formatadores de modulo leem daqui,
  // e o mesmo vale para o dicionario que o codigo fora do React consome
  // (templates de projeto sao semeados por servico, nao por componente).
  fmt.setCurrentLocale(locale);
  setCurrentCopy(copy);

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[locale];
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    persistLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, copy, href: (path: string) => localizedPath(path, locale), setLocale }),
    [locale, copy, setLocale],
  );

  // Enquanto o dicionário do idioma pedido não chegou, não renderiza: mostrar o
  // português e trocar depois daria um flash de idioma errado logo na primeira
  // impressão. Para pt-BR isso nunca acontece, porque é estático.
  if (!ready) return null;

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** Fora do provider devolve português, para nenhum componente quebrar isolado. */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  return { locale: DEFAULT_LOCALE, copy: pt, href: (p) => p, setLocale: () => {} };
}

/** Atalho para quem só precisa dos textos. */
export function useCopy(): Dictionary {
  return useLocale().copy;
}

/** Interpolação mínima de `{{chave}}`. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) => String(vars[k] ?? ''));
}

export type { Dictionary };

/**
 * Formatadores já amarrados ao idioma atual.
 *
 * Existe para o call site não ter que carregar `locale` em toda chamada: com
 * ~69 pontos de formatação espalhados, passar o idioma manualmente em cada um
 * seria ruído e, pior, um lugar a esquecer.
 */
export function useFormat() {
  const { locale } = useLocale();
  return useMemo(() => ({
    locale,
    date: (v: Date | string | number | null | undefined, o?: Intl.DateTimeFormatOptions) => fmt.formatDate(v, locale, o),
    dateTime: (v: Date | string | number | null | undefined) => fmt.formatDateTime(v, locale),
    time: (v: Date | string | number | null | undefined) => fmt.formatTime(v, locale),
    number: (v: number | null | undefined, o?: Intl.NumberFormatOptions) => fmt.formatNumber(v, locale, o),
    currency: (v: number | null | undefined, c?: string) => fmt.formatCurrency(v, locale, c),
    plural: (n: number, forms: fmt.PluralForms) => fmt.plural(n, forms, locale),
    pluralize: (n: number, forms: fmt.PluralForms) => fmt.pluralize(n, forms, locale),
    monthNames: (style?: 'long' | 'short') => fmt.monthNames(locale, style),
    weekdayNames: (style?: 'long' | 'short' | 'narrow') => fmt.weekdayNames(locale, style),
    collator: () => fmt.collator(locale),
  }), [locale]);
}
